class FacetFilters {
    constructor() {
        this.debouncedOnSubmit = debounce(this.onSubmitHandler.bind(this), 500);

        // Add event listeners for filters
        this.attachFacetListeners();

        // Attach pagination event listeners
        this.attachPaginationEventListeners();

        // Set initial filters and sorting
        this.setInitialFilters();

        // Attach mobile facets toggle
        this.attachMobileFacetsToggle();

        // Attach sorting functionality
        this.attachSortListener();
    }

    onSubmitHandler(event) {
        event.preventDefault();
        this.applyFilters();
    }

    attachFacetListeners() {
        const facetForm = document.querySelectorAll('.facet-filter');
        facetForm.forEach(input => {
            input.addEventListener('change', () => {
                const filterType = input.getAttribute('data-filter-type');
                const queryParams = new URLSearchParams(window.location.search);

                if (!input.checked) {
                    // Remove filters from URL when unchecked
                    if (filterType === 'price') {
                        queryParams.delete('filter.v.price.gte');
                        queryParams.delete('filter.v.price.lte');
                    } else if (filterType === 'vendor') {
                        const vendors = queryParams.getAll('filter.p.vendor').filter(v => v !== input.getAttribute('data-filter-value'));
                        queryParams.delete('filter.p.vendor');
                        vendors.forEach(v => queryParams.append('filter.p.vendor', v));
                    } else {
                        queryParams.delete(filterType);
                    }
                } else {
                    // Add filters to URL when checked
                    if (filterType === 'price') {
                        const gte = input.getAttribute('data-filter-gte');
                        const lte = input.getAttribute('data-filter-lte');
                        queryParams.set('filter.v.price.gte', gte);
                        queryParams.set('filter.v.price.lte', lte);
                    } else if (filterType === 'vendor') {
                        queryParams.append('filter.p.vendor', input.getAttribute('data-filter-value'));
                    } else {
                        queryParams.append(filterType, input.getAttribute('data-filter-value'));
                    }
                }

                window.history.replaceState({}, '', '?' + queryParams.toString());
                this.applyFilters(); // Apply the filters once the URL is updated
            });
        });

        const clearAllBtn = document.querySelector('.js-clear-all-facets');
        if (clearAllBtn) {
            clearAllBtn.addEventListener('click', () => this.clearAllFilters());
        }

        this.attachRemoveFilterListener();
    }

    applyFilters() {
        const queryParams = new URLSearchParams(window.location.search);

        // Reset pagination when filters are applied
        queryParams.delete('page');

        // Call renderPage to update both product grid and facets
        this.renderPage(queryParams);
        // Re-attach Sort Listener
        this.attachSortListener();
    }

    renderPage(queryParams) {
        // Determine whether we're on the search page or a regular page
        const isSearchPage = window.location.pathname.includes('/search');

        // Adjust the product grid section ID based on the URL
        const productGridSection = isSearchPage ? 'product-grid-search' : 'product-grid';

        // URLs for fetching the product grid and facets sections
        const productGridUrl = `${window.location.pathname}?${queryParams.toString()}&section_id=${productGridSection}`;
        const facetsUrl = `${window.location.pathname}?${queryParams.toString()}&section_id=facets`;

        // Fetch product grid
        fetch(productGridUrl)
            .then(response => {
                //console.log(response);

                if (!response.ok) {
                    throw new Error('Product Grid section not found');
                }
                return response.text();
            })
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newProductGrid = doc.querySelector(`#${productGridSection}`);

                // Update the product grid
                if (newProductGrid) {
                    document.querySelector(`#${productGridSection}`).innerHTML = newProductGrid.innerHTML;
                    //console.log("Product grid updated");
                }

                // Fetch facets section after updating the product grid
                return fetch(facetsUrl);
            })
            .then(response => {
                //console.log(response);

                if (!response.ok) {
                    throw new Error('Facets section not found');
                }
                return response.text();
            })
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newFacets = doc.querySelector('#facets');

                // Update the facets section
                if (newFacets) {
                    document.querySelector('#facets').innerHTML = newFacets.innerHTML;
                    //console.log("Facets section updated");
                }

                // Reattach all necessary event listeners
                this.attachFacetListeners();
                this.attachPaginationEventListeners();
                this.attachRemoveFilterListener();
                this.updateActiveFilters(queryParams);
                this.reInitializeWidgets();
                this.attachSortListener();

                initProductImageRollovers();


                // Update the URL in the address bar
                window.history.replaceState({}, '', '?' + queryParams.toString());
            })
            .catch(error => console.error('Error fetching the updated product grid or facets:', error));
    }

    updateActiveFilters(queryParams) {
        const activeFiltersUl = document.querySelector('.js-facet-current-filters-ul');
        activeFiltersUl.innerHTML = ''; // Clear previous filters

        // Handle price filter as a combined filter
        const priceGte = queryParams.get('filter.v.price.gte');
        const priceLte = queryParams.get('filter.v.price.lte');
        if (priceGte && priceLte) {
            const filterLi = document.createElement('li');
            filterLi.classList.add('facet-current-filters-li', 'js-facet-current-filters-li');
            filterLi.setAttribute('data-filter-type', 'price');
            filterLi.innerHTML = `<span>Price: £${priceGte} - £${priceLte}</span>`;
            activeFiltersUl.appendChild(filterLi);
        }

        // Handle other filters
        queryParams.forEach((value, key) => {
            if (key.startsWith('filter.') && key !== 'filter.v.price.gte' && key !== 'filter.v.price.lte') {
                const filterElement = document.querySelector(`.facet-filter[data-filter-value="${value}"]`);
                const filterLi = document.createElement('li');
                const filterName = filterElement ? filterElement.getAttribute('data-filter-name') : 'Filter';
                filterLi.classList.add('facet-current-filters-li', 'js-facet-current-filters-li');
                filterLi.setAttribute('data-filter-type', key);
                filterLi.innerHTML = `<span>${filterName}: ${decodeURIComponent(value)}</span>`;
                activeFiltersUl.appendChild(filterLi);
            }
        });

        document.querySelector('.facet-current-filters').style.display = activeFiltersUl.children.length > 0 ? 'block' : 'none';
    }

    attachRemoveFilterListener() {
        const activeFiltersContainer = document.querySelector('.js-facet-current-filters-ul');
        if (activeFiltersContainer) {
            activeFiltersContainer.addEventListener('click', (event) => {
                const filterElement = event.target.closest('.js-facet-current-filters-li');
                if (filterElement) {
                    this.removeFilter(filterElement);
                }
            });
        }
    }

    removeFilter(li) {
        const filterType = li.getAttribute('data-filter-type');
        const queryParams = new URLSearchParams(window.location.search);
        //console.log("removeFilter: " + filterType);
        if (filterType === 'price') {
            // Ensure both parts of the price filter are removed
            queryParams.delete('filter.v.price.gte');
            queryParams.delete('filter.v.price.lte');
        } else {
            queryParams.delete(filterType);
        }
        this.renderPage(queryParams);
    }

    clearAllFilters() {
        console.log("clearAllFilters Called");
        // Uncheck all selected filters
        document.querySelectorAll('.facet-filter:checked').forEach(input => {
            input.checked = false;
        });

        // Clear filters from the URL parameters
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.forEach((value, key) => {
            if (key.startsWith('filter.')) {
                queryParams.delete(key);
            }
        });

        // Make sure ALL price filters are removed.
        queryParams.delete('filter.v.price.gte');
        queryParams.delete('filter.v.price.lte');

        // Reset the page and reapply filters
        queryParams.delete('page');

        // Update the URL and product grid dynamically
        this.renderPage(queryParams);
    }

    /*
    attachPaginationEventListeners() {
        document.querySelectorAll('.js-pagination a').forEach(link => {
            link.addEventListener('click', event => {
                event.preventDefault();
                const pageUrl = new URL(link.href);
                const queryParams = new URLSearchParams(window.location.search);
                const currentUrl = window.location.href;
                const pageString = '&page=';

                queryParams.set('page', pageUrl.searchParams.get('page'));

                console.log("New URL: ", currentUrl + pageString + pageUrl.searchParams.get('page'));
                console.log(pageUrl.searchParams.get('page'));

                //this.renderPage(queryParams);
            });
        });
    }
     */

    attachPaginationEventListeners() {
        document.querySelectorAll('.js-pagination a').forEach(link => {
            link.addEventListener('click', event => {
                event.preventDefault();

                // Get the page number from the pagination link that was clicked
                const pageUrl = new URL(link.href);
                const newPage = pageUrl.searchParams.get('page'); // e.g., '4'

                // Get the current URL and query parameters
                const currentUrl = new URL(window.location.href);
                const queryParams = new URLSearchParams(currentUrl.search);

                // Check if the current URL already has a 'page' parameter
                if (queryParams.has('page')) {
                    // If it exists, update the page number with the new value
                    queryParams.set('page', newPage);
                } else {
                    // If it doesn't exist, add the 'page' parameter with the new value
                    queryParams.append('page', newPage);
                }

                // Update the URL without reloading the page
                const updatedUrl = `${currentUrl.pathname}?${queryParams.toString()}`;
                window.history.replaceState({}, '', updatedUrl);


                let scrollOffset;
                if (screen.availWidth < 700) {
                    // Mobile
                    const targetEl = document.querySelector('.js-pagination');
                    if (targetEl) {
                        const rect = targetEl.getBoundingClientRect();
                        scrollOffset = window.scrollY + rect.top;
                    } else {
                        // Desktop
                        scrollOffset = 0;
                    }
                } else {
                    scrollOffset = 0;
                }

                window.scrollTo({
                    top: scrollOffset,
                    behavior: 'smooth'
                });


                // Optional: Log the updated URL for debugging
                //console.log(`Updated URL: ${updatedUrl}`);

                // Render the page with the updated query parameters
                this.renderPage(queryParams);
            });
        });
    }

    attachSortListener() {
        const sortByDropdown = document.getElementById('sort-by-dropdown');

        if (sortByDropdown) {
            // Set the dropdown value based on the current URL
            const queryParams = new URLSearchParams(window.location.search);
            const currentSort = queryParams.get('sort_by');

            if (currentSort) {
                sortByDropdown.value = currentSort; // Set the dropdown to the current sort
            }

            // Add event listener for dropdown changes
            sortByDropdown.addEventListener('change', () => {
                console.log("SORT CHANGE");

                const selectedSort = sortByDropdown.value;

                if (selectedSort && selectedSort !== 'manual') {
                    queryParams.set('sort_by', selectedSort);
                } else {
                    queryParams.delete('sort_by');
                }

                // Update URL and refresh product grid with the selected sorting
                window.history.replaceState({}, '', '?' + queryParams.toString());
                this.applyFilters();
            });
        }
    }

    setInitialFilters() {
        const queryParams = new URLSearchParams(window.location.search);

        document.querySelectorAll('.facet-filter').forEach(input => {
            const filterType = input.getAttribute('data-filter-type');
            const filterValue = input.getAttribute('data-filter-value');
            const gte = input.getAttribute('data-filter-gte');
            const lte = input.getAttribute('data-filter-lte');

            // Handle price filters
            if (filterType === 'price') {
                if (queryParams.get('filter.v.price.gte') === gte && queryParams.get('filter.v.price.lte') === lte) {
                    input.checked = true;
                }
            }

            // Handle vendor and other filters
            if (filterType !== 'price' && queryParams.get(`filter.${filterType}`) === filterValue) {
                input.checked = true;
            }
        });
    }

    reInitializeWidgets() {
        //console.log("WIDGET INIT");
        // Assuming you have widgets like Yotpo that need re-initialization
        if (typeof yotpoWidgetsContainer !== 'undefined' && typeof yotpoWidgetsContainer.initWidgets === 'function') {
            yotpoWidgetsContainer.initWidgets();
        }
    }

    attachMobileFacetsToggle() {
        const mobileFacetsBtn = document.querySelector('.js-facet-mobile-btn');
        const facetsContainer = document.querySelector('.js-facets');

        if (mobileFacetsBtn && facetsContainer) {
            mobileFacetsBtn.addEventListener('click', () => {
                facetsContainer.style.maxHeight = facetsContainer.classList.contains('show') ? null : facetsContainer.scrollHeight + 'px';
                facetsContainer.classList.toggle('show');
            });
        }
    }
}

// Debounce function to avoid rapid requests
function debounce(func, wait) {
    let timeout;
    return function (...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

function initProductImageRollovers(scope = document) {

    const configEl = document.querySelector('[data-section-config]');
    if (!configEl) return;

    const config = JSON.parse(configEl.textContent);

    if (config.rolloverEnabled) {

        scope.querySelectorAll('.collection-image[data-rollover]').forEach(img => {
            // Prevent double-binding
            if (img.dataset.rolloverInit) return;
            img.dataset.rolloverInit = 'true';

            const originalSrc = img.getAttribute('data-src') || img.src;
            const rolloverSrc = img.getAttribute('data-rollover');

            if (!rolloverSrc) return;

            // Preload rollover image
            const preloadImg = new Image();
            preloadImg.src = rolloverSrc;

            img.addEventListener('mouseenter', () => {
                img.src = rolloverSrc;
            });

            img.addEventListener('mouseleave', () => {
                img.src = originalSrc;
            });
        });
    }

}


// Initialize the facet filters
document.addEventListener('DOMContentLoaded', () => {
    new FacetFilters();
    initProductImageRollovers();
});